import { DestroyRef, Injectable, ViewContainerRef, effect, inject, signal, untracked } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, Router } from '@angular/router';
import { BrnDialogService } from '@spartan-ng/brain/dialog';
import { EMPTY, distinctUntilChanged, filter, finalize, firstValueFrom, map, startWith, switchMap, take } from 'rxjs';
import { NotificationsStore } from '@lite-workspace/shared/blocks/notifications';
import { RetouchRuns, retouchEditorProjectOf } from '@lite-workspace/shared/retouch/host';
import { RETOUCH_EVENTS } from '@lite-workspace/shared/retouch/ports';
import { BalancePopupsLauncherService } from '../features/account/ui/balance-popups-launcher.service';
import { ProjectsFacade } from '../features/projects/domain/application/projects.facade';
import {
  RetouchDoneDialogComponent,
  type RetouchDoneDialogContext,
  type RetouchDoneResult,
} from './retouch-done-dialog.component';

export const RETOUCH_RUN_DONE = 'retouch.run_done';
/** More than the strip ever shows: the row clips the rest. */
const THUMB_READS = 8;
/** The run's inbox item lands after its FINISHED (Kafka → notifications service): a few spaced re-reads. */
export const RUN_READ_TRIES = 5;
export const RUN_READ_GAP_MS = 2000;

interface RetouchRunDto {
  projectId: string;
  newFileIds: string[];
  count: number;
  status: string;
}

/** The project a cabinet url stands in, `null` outside any project. */
export function projectIdOf(url: string): string | null {
  return /^\/projects\/([^/?#]+)/.exec(url)?.[1] ?? null;
}

/**
 * `/projects/<id>/photos?filter=retouch-done&run=<processId>` (or the older `/projects/<id>/retouch?run=` still in
 * the inbox) → its two ids; any other link is not a run.
 */
export function parseRunLink(url: string | null): { projectId: string; runId: string } | null {
  if (!url) return null;
  const parsed = new URL(url, 'http://cabinet');
  const [, projectId, page] = /^\/projects\/([^/]+)\/(retouch|photos)$/.exec(parsed.pathname) ?? [];
  const runId = parsed.searchParams.get('run');
  const runPage = page === 'retouch' || parsed.searchParams.get('filter') === 'retouch-done';
  return projectId && runId && runPage ? { projectId, runId } : null;
}

/**
 * A retouch run ends while the photographer is anywhere — another project, another day. Its unread
 * `retouch.run_done` notification is the record: the window waits for him on every cabinet page until he
 * closes it, and closing it reads the notification.
 */
@Injectable({ providedIn: 'root' })
export class RetouchDoneLauncherService {
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(BrnDialogService);
  private readonly router = inject(Router);
  private readonly notifications = inject(NotificationsStore);
  private readonly facade = inject(ProjectsFacade);
  private readonly balancePopups = inject(BalancePopupsLauncherService);
  private readonly events = inject(RETOUCH_EVENTS, { optional: true });
  private readonly runs = inject(RetouchRuns);

  private readonly cabinet = signal<ViewContainerRef | null>(null);
  /** A read or an open window: one window at a time, the next run waits for this one's close. */
  private busy = false;
  /** Closed this visit — a failed `markRead` must not bring the same window straight back. */
  private readonly closedIds = new Set<string>();
  /** Projects with a run of this session in flight. */
  private readonly followed = new Set<string>();
  /** Runs the editor's done toast already told him of: their window never opens, the item is read. */
  private readonly handledRuns = new Set<string>();

  constructor() {
    // Cabinet mount, the balance window leaving the container, every move of the bell's 60 s poll.
    effect(() => {
      this.notifications.unreadCount();
      if (this.cabinet() && !this.balancePopups.popupOpen()) untracked(() => void this.announce());
    });
    // A batch the editor started: followed to its FINISHED wherever he goes meanwhile.
    let launched = this.runs.launched();
    effect(() => {
      const run = this.runs.launched();
      if (run === launched) return;
      launched = run;
      if (run) untracked(() => this.follow(run.projectId));
    });
  }

  /** Called by the cabinet shell — its view container hosts the window and dies with the cabinet. */
  registerCabinet(vcr: ViewContainerRef, destroyRef: DestroyRef): void {
    this.cabinet.set(vcr);
    destroyRef.onDestroy(() => this.cabinet.set(null));
    // The open project's FINISHED beats the poll by up to a minute.
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(null),
        map(() => projectIdOf(this.router.url)),
        distinctUntilChanged(),
        switchMap(projectId => (projectId && this.events ? this.events(projectId) : EMPTY)),
        filter(event => event.type === 'RETOUCH_FINISHED'),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe(() => void this.announce());
  }

  // ponytail: one follow per project, its first FINISHED ends it; a second run of that project in flight then waits
  // for the bell poll. A per-run follow when that bites.
  private follow(projectId: string | null): void {
    if (!projectId || !this.events || this.followed.has(projectId)) return;
    this.followed.add(projectId);
    this.events(projectId)
      .pipe(
        filter(event => event.type === 'RETOUCH_FINISHED'),
        take(1),
        finalize(() => this.followed.delete(projectId)),
      )
      .subscribe({
        // In that project's editor the done toast is the signal.
        next: () => void (retouchEditorProjectOf(this.router.url) === projectId ? undefined : this.announceSoon()),
        error: () => undefined,
      });
  }

  private async announceSoon(): Promise<void> {
    for (let attempt = 0; attempt < RUN_READ_TRIES; attempt++) {
      if (attempt) await new Promise(resolve => setTimeout(resolve, RUN_READ_GAP_MS));
      if (await this.announce()) return;
    }
  }

  /** Resolves true when a window opened. */
  private async announce(): Promise<boolean> {
    const vcr = this.cabinet();
    if (!vcr || this.busy) return false;
    this.busy = true;
    const next = await this.read().catch(() => null);
    // Nothing to show, the cabinet died or the balance window took the container while on the wire:
    // the next trigger asks again.
    if (!next || this.cabinet() !== vcr || this.balancePopups.popupOpen()) {
      this.busy = false;
      return false;
    }

    const ref = this.dialog.open<RetouchDoneDialogContext, RetouchDoneResult>(RetouchDoneDialogComponent, vcr, next.ctx, {
      hasBackdrop: true,
    });
    ref.closed$.subscribe(result => {
      this.closedIds.add(next.id);
      void this.notifications.markRead(next.id);
      this.busy = false;
      if (result === 'open') void this.router.navigateByUrl(next.link);
      void this.announce();
    });
    return true;
  }

  markHandled(runId: string): void {
    this.handledRuns.add(runId);
  }

  /** One run: its project, count and the new ids of the photos it applied. */
  readRun(projectId: string, runId: string): Promise<RetouchRunDto> {
    // ponytail: a tiny app-level read until the shared retouch api carries `runs/:id`.
    return firstValueFrom(this.http.get<RetouchRunDto>(`/api/projects/${projectId}/retouch/runs/${runId}`));
  }

  private async read(): Promise<{ id: string; link: string; ctx: RetouchDoneDialogContext } | null> {
    const unread = await this.notifications.unreadOfType(RETOUCH_RUN_DONE);
    for (const item of unread) {
      const link = parseRunLink(item.linkUrl);
      if (!link || this.closedIds.has(item.id)) continue;
      const shown = await this.window(item.id, link);
      // The project is gone, or the toast told him (checked after the read: the editor may report the run meanwhile):
      // nothing to open, the inbox item is read silently and the next run takes the window.
      if (shown === 'gone' || this.handledRuns.has(link.runId)) {
        this.closedIds.add(item.id);
        void this.notifications.markRead(item.id);
      } else if (shown) return shown;
      // null — its project name is unknown yet: it waits for the next ask, the next run is not held behind it.
    }

    return null;
  }

  private async window(
    id: string,
    link: { projectId: string; runId: string },
  ): Promise<{ id: string; link: string; ctx: RetouchDoneDialogContext } | null | 'gone'> {
    const run = await this.readRun(link.projectId, link.runId).catch((error: unknown) => {
      // The run's own route: 404 — not in the project, 403 — the project is no longer his. Anything else waits.
      if (error instanceof HttpErrorResponse && (error.status === 403 || error.status === 404)) return null;
      throw error;
    });
    if (!run) return 'gone';
    const thumbs = await this.thumbs(run);
    // The form names the page the window opens on, not the one it was asked from: the previous window's
    // «Открыть» is still on its way into the project.
    await this.routerIdle();
    const here = projectIdOf(this.router.url) === run.projectId;
    const projectName = here ? null : await this.projectName(run.projectId);
    // Elsewhere the window must name the project it opens.
    if (!here && !projectName) return null;

    // Built from the ids, never the stored link: older inbox items still point at the retired retouch page.
    return {
      id,
      link: `/projects/${link.projectId}/photos?filter=retouch-done&run=${link.runId}`,
      ctx: { count: run.count, thumbs, projectName },
    };
  }

  /** A redirect starts its navigation before the cancelled one settles, so the loop waits the chain out. */
  private async routerIdle(): Promise<void> {
    while (this.router.currentNavigation()) {
      await firstValueFrom(
        this.router.events.pipe(
          filter(
            event =>
              event instanceof NavigationEnd ||
              event instanceof NavigationCancel ||
              event instanceof NavigationError ||
              event instanceof NavigationSkipped,
          ),
        ),
      );
    }
  }

  private async projectName(projectId: string): Promise<string | null> {
    const find = (): string | null => this.facade.projectById(projectId)?.name ?? null;
    if (find() || this.facade.projectsLoaded()) return find();
    await this.facade.joinProjectsLoad().catch(() => undefined);
    return find();
  }

  private async thumbs(run: RetouchRunDto): Promise<string[]> {
    const held = new Map(this.facade.heldPhotos(run.projectId).map(photo => [photo.id, photo]));
    const photos = await Promise.all(
      run.newFileIds.slice(0, THUMB_READS).map(id => held.get(id) ?? this.facade.getPhoto(run.projectId, id).catch(() => null)),
    );
    return photos.flatMap(photo => (photo?.thumbUrl ? [photo.thumbUrl] : []));
  }
}
